import React, { useEffect, useCallback, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Info } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from 'src/redux/store';
import type { RootState } from 'src/redux/rootReducerType';
import { useDataVault } from 'src/contexts/DataVaultContext';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import {
  fetchSectionAnswers,
  invalidateSection,
  selectSectionAnswers,
} from 'src/redux/slices/DataVault/dataVaultSectionDetailSlice';
import { selectSections } from 'src/redux/slices/DataVault/dataVaultSectionsSlice';
import { QITAnswerRow } from 'src/components/NewRenterInterface/DataVault/components/QITAnswerRow';
import { SelectionActionBar } from 'src/components/NewRenterInterface/DataVault/components/SelectionActionBar';
import { useSelectionMode } from 'src/hooks/DataVaultHooks/useSelectionMode';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import {
  NAV_WIDTH_COLLAPSED,
  NAV_WIDTH_EXPANDED,
} from 'src/components/NewRenterInterface/NavBar/NavBarConstants';

export function PQSectionDetailPage(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const navigate = useNavigate();
  const { sectionId } = useParams<{ sectionId: string }>();
  const [searchParams] = useSearchParams();
  const { targetUserId, actingOnMemberId, isOnBehalf } = useDataVault();
  const { enabled, selected, enter, exit, toggle } = useSelectionMode();
  const bottomNavHeight = useSelector(
    (state: RootState) => state.newRenterUI.bottomNavHeight
  );
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navExpanded = useSelector(
    (state: RootState) => state.newRenterUI.expanded
  );
  const fixedLeft = isMobile
    ? 0
    : navExpanded
    ? NAV_WIDTH_EXPANDED
    : NAV_WIDTH_COLLAPSED;

  const [bottomBarHeight, setBottomBarHeight] = useState(0);

  const measureBottomBar = useCallback((el: HTMLElement | null) => {
    if (el) {
      setBottomBarHeight(el.getBoundingClientRect().height);
    } else {
      setBottomBarHeight(0);
    }
  }, []);

  const resolvedSectionId = sectionId ?? '';
  const answersEntry = useSelector(
    selectSectionAnswers(targetUserId, resolvedSectionId)
  );
  const sectionsEntry = useSelector(selectSections(targetUserId));
  const sectionName =
    sectionsEntry?.sections?.find((s) => s.id === resolvedSectionId)?.name ??
    'Section';

  // Enter selection mode if ?select=1 is in the URL
  useEffect(() => {
    if (searchParams.get('select') === '1') {
      enter();
    }
  }, [searchParams, enter]);

  // Invalidate on mount so stale cache from other flows is cleared immediately.
  useEffect(() => {
    if (!resolvedSectionId) {
      return;
    }
    dispatch(invalidateSection({ targetUserId, sectionId: resolvedSectionId }));
  }, [dispatch, targetUserId, resolvedSectionId]);

  // Re-fetch whenever the entry is absent (covers mount + WS invalidation).
  useEffect(() => {
    if (!resolvedSectionId || answersEntry !== undefined) {
      return;
    }
    dispatch(
      fetchSectionAnswers({
        targetUserId,
        sectionId: resolvedSectionId,
        actingOnMemberId,
      })
    );
  }, [
    dispatch,
    targetUserId,
    resolvedSectionId,
    actingOnMemberId,
    answersEntry,
  ]);

  const pqIndexPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}/pq`
    : '/renter/data-vault/pq';

  const onSubmit = useCallback(() => {
    const qits = Array.from(selected)
      .sort((a, b) => a - b)
      .join(',');
    const base = isOnBehalf
      ? `/renter/data-vault/members/${actingOnMemberId}/pq/sections/${resolvedSectionId}/answer`
      : `/renter/data-vault/pq/sections/${resolvedSectionId}/answer`;
    dispatch(interviewActions.clearAllDeferred());
    navigate(`${base}?qits=${qits}`);
  }, [
    selected,
    isOnBehalf,
    actingOnMemberId,
    resolvedSectionId,
    navigate,
    dispatch,
  ]);

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------

  if (answersEntry?.status === 'loading') {
    return (
      <Box
        aria-busy="true"
        aria-label="Loading section answers"
        sx={{ bgcolor: 'background.default', minHeight: '100vh' }}
      >
        <PageHeader title={sectionName} backRoute={pqIndexPath} />
        <Box
          sx={{
            bgcolor: 'background.paper',
            mx: 2,
            mt: 2,
            borderRadius: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
            border: `1px solid ${theme.palette.divider}`,
            overflow: 'hidden',
          }}
        >
          {[0, 1, 2, 3, 4].map((placeholder) => (
            <Box
              key={placeholder}
              sx={{
                px: 2,
                py: 1.75,
                borderBottom: placeholder < 4 ? '1px solid' : 'none',
                borderColor: 'divider',
              }}
            >
              <Skeleton
                variant="text"
                width="35%"
                sx={{ fontSize: 12 }}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="60%"
                sx={{ fontSize: 14 }}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Error
  // ---------------------------------------------------------------------------

  if (answersEntry?.status === 'failed') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography
          component={Link}
          to={pqIndexPath}
          sx={{
            color: 'primary.main',
            textDecoration: 'none',
            fontSize: 14,
            display: 'inline-block',
            mb: 3,
          }}
        >
          &larr; Back
        </Typography>
        <Typography
          role="alert"
          sx={{
            color: 'text.secondary',
            fontSize: 14,
          }}
        >
          Failed to load answers.{' '}
          <Button
            size="small"
            variant="outlined"
            onClick={() =>
              dispatch(
                fetchSectionAnswers({
                  targetUserId,
                  sectionId: resolvedSectionId,
                  actingOnMemberId,
                  forceRefresh: true,
                })
              )
            }
            sx={{ textTransform: 'none', ml: 1 }}
          >
            Retry
          </Button>
        </Typography>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Content
  // ---------------------------------------------------------------------------

  const rows = answersEntry?.rows ?? [];

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        pb: `${bottomBarHeight}px`,
      }}
    >
      <PageHeader title={sectionName} backRoute={pqIndexPath} />

      {/* Selection mode hint banner */}
      {enabled && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            bgcolor: alpha(theme.palette.warning.main, 0.12),
            borderBottom: `1px solid ${theme.palette.warning.main}`,
            px: 2,
            py: 1.25,
          }}
        >
          <Info size={16} color={theme.palette.warning.dark} />
          <Typography
            sx={{
              color: theme.palette.text.primary,
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Select the answers that need to be corrected
          </Typography>
        </Stack>
      )}

      {/* Answer list */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          mx: 2,
          mt: 2,
          mb: 2,
          borderRadius: '16px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.10)',
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {rows.map((answer, index) => (
          <QITAnswerRow
            key={answer.qitId}
            qit={{ id: answer.qitId, label: answer.label }}
            answer={answer}
            selectionMode={enabled}
            isSelected={selected.has(String(answer.qitId))}
            onToggleSelect={toggle}
            hideBorder={index === rows.length - 1}
          />
        ))}
      </Box>

      {/* Bottom CTA — fixed above the bottom nav bar */}
      {!enabled && (
        <Box
          ref={measureBottomBar}
          sx={{
            position: 'fixed',
            bottom: isMobile ? bottomNavHeight : 0,
            left: fixedLeft,
            right: 0,
            bgcolor: theme.palette.background.paper,
            py: 1.5,
            px: 2,
            zIndex: 10,
          }}
        >
          <Button
            fullWidth
            onClick={enter}
            sx={{
              height: 44,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'grey.300',
              bgcolor: theme.palette.background.paper,
              color: 'text.secondary',
              fontSize: 14,
              fontWeight: 500,
              textTransform: 'none',
            }}
          >
            I want to update my answers
          </Button>
        </Box>
      )}
      {enabled && (
        <SelectionActionBar
          ref={measureBottomBar}
          selectedCount={selected.size}
          onSubmit={onSubmit}
          onCancel={exit}
        />
      )}
    </Box>
  );
}
